import { extractVars, validateVarsByType, validateVariableSpaces, validateTemplateName } from '../TemplateUtils';

const startsOrEndsWithVariable = (text = '') => {
  const t = String(text || '');
  const startsWithVar = /^\s*{{\s*([^{}]+?)\s*}}/.test(t);
  const endsWithVar = /{{\s*([^{}]+?)\s*}}\s*$/.test(t);
  return startsWithVar || endsWithVar;
};

export const validateAuthTemplateForm = (formData) => {
  const errors = {};

  if (!formData.name.trim()) {
    errors.name = 'Template name is required';
  } else {
    const nameError = validateTemplateName(formData.name);
    if (nameError) errors.name = nameError;
  }

  if (!formData.category) errors.category = 'Category is required';
  if (!formData.language) errors.language = 'Language is required';

  // Message TTL: accept camelCase or snake_case keys
  const ttlRaw = formData.messageSendTtlSeconds ?? formData.message_send_ttl_seconds ?? formData.message_send_ttl_seconds;
  const ttl = ttlRaw === undefined || ttlRaw === null || String(ttlRaw).trim() === '' ? null : Number(ttlRaw);
  if (ttl !== null) {
    if (!Number.isInteger(ttl) || ttl < 30 || ttl > 900) {
      errors.messageSendTtlSeconds = `message_send_ttl_seconds must be an integer between 30 and 900 (30 seconds to 15 minutes). Got: ${ttlRaw}`;
    }
  }

  // Code expiration: accept several possible keys
  const codeRaw = formData.codeExpirationMinutes ?? formData.code_expiration_minutes ?? formData.expiration_minutes ?? formData.expirationMinutes;
  const codeExp = codeRaw === undefined || codeRaw === null || String(codeRaw).trim() === '' ? null : Number(codeRaw);
  if (codeExp !== null) {
    if (!Number.isInteger(codeExp) || codeExp < 1 || codeExp > 90) {
      errors.codeExpirationMinutes = `code_expiration_minutes / expiration_minutes must be an integer between 1 and 90. Got: ${codeRaw}`;
    }
  }

  return errors;
};

export const validateRegularTemplateForm = (formData) => {
  const errors = {};

  if (!formData.name.trim()) {
    errors.name = 'Template name is required';
  } else {
    const nameError = validateTemplateName(formData.name);
    if (nameError) errors.name = nameError;
  }

  if (!formData.category) errors.category = 'Category is required';
  if (!formData.language) errors.language = 'Language is required';
  if (!formData.bodyText.trim()) errors.bodyText = 'Body text is required';

  if (formData.headerText && startsOrEndsWithVariable(formData.headerText)) {
    errors.headerText = 'Variables cannot be at the start or end of the template.';
  }

  if (startsOrEndsWithVariable(formData.bodyText)) {
    errors.bodyText = 'Variables cannot be at the start or end of the template.';
  }

  const headerSpaceError = validateVariableSpaces(formData.headerText);
  if (headerSpaceError) errors.headerText = headerSpaceError;

  const bodySpaceError = validateVariableSpaces(formData.bodyText);
  if (bodySpaceError) errors.bodyText = bodySpaceError;

  // Buttons validation
  const buttons = Array.isArray(formData.buttons) ? formData.buttons : [];
  if (buttons.length) {
    if (buttons.length > 10) errors.buttons = 'Maximum 10 buttons are allowed.';

    const copyCount = buttons.filter(b => b.type === 'COPY').length;
    if (copyCount > 1) errors.buttons = 'Only one Copy Code button is allowed.';

    const quickCount = buttons.filter(b => b.type === 'QUICK_REPLY').length;
    const ctaCount = buttons.filter(b => ['URL', 'CALL', 'COPY'].includes(b.type)).length;
    if (quickCount > 0 && ctaCount > 0) errors.buttons = 'Quick Reply buttons cannot be mixed with CTA buttons.';

    // per-button validations
    for (let i = 0; i < buttons.length; i += 1) {
      const b = buttons[i] || {};
      const idx = i + 1;
      if (b.type === 'QUICK_REPLY') {
        if (!b.text || String(b.text).trim() === '') {
          errors.buttons = `Quick Reply #${idx}: text is required (max 25 chars).`;
          break;
        }
        if (String(b.text).length > 25) {
          errors.buttons = `Quick Reply #${idx}: text must be 25 characters or less.`;
          break;
        }
      }
      if (b.type === 'URL') {
        if (!b.text || String(b.text).trim() === '') {
          errors.buttons = `URL Button #${idx}: text is required (max 25 chars).`;
          break;
        }
        if (String(b.text).length > 25) {
          errors.buttons = `URL Button #${idx}: text must be 25 characters or less.`;
          break;
        }
        if (!b.url || String(b.url).trim() === '') {
          errors.buttons = `URL Button #${idx}: URL is required.`;
          break;
        }
        if (String(b.url).length > 2000) {
          errors.buttons = `URL Button #${idx}: URL must be 2000 characters or less.`;
          break;
        }
        try {
          // Ensure URL constructor accepts it (allow template placeholder too)
          const testUrl = String(b.url).replace(/\{\{(\d+)\}\}/g, '1');
          /* eslint-disable no-new */
          new URL(testUrl);
        } catch (e) {
          errors.buttons = `URL Button #${idx}: URL format is invalid.`;
          break;
        }
        if (String(b.url).match(/\{\{(\d+)\}\}/)) {
          // dynamic URL rules: exactly one placeholder and must be at the end
          const matches = String(b.url).match(/\{\{(\d+)\}\}/g) || [];
          if (matches.length !== 1) {
            errors.buttons = `URL Button #${idx}: dynamic URL must contain exactly one {{number}} placeholder.`;
            break;
          }
          if (!String(b.url).trim().match(/\{\{(\d+)\}\}\s*$/)) {
            errors.buttons = `URL Button #${idx}: {{number}} placeholder must be at the end of the URL.`;
            break;
          }
          if (!b.urlExample || String(b.urlExample).trim() === '') {
            errors.buttons = `URL Button #${idx}: example value is required for dynamic URLs.`;
            break;
          }
        }
      }
      if (b.type === 'CALL') {
        if (!b.text || String(b.text).trim() === '') {
          errors.buttons = `Call Button #${idx}: text is required (max 25 chars).`;
          break;
        }
        if (String(b.text).length > 25) {
          errors.buttons = `Call Button #${idx}: text must be 25 characters or less.`;
          break;
        }
        if (!b.phoneNumber || String(b.phoneNumber).trim() === '') {
          errors.buttons = `Call Button #${idx}: phone number is required.`;
          break;
        }
        if (String(b.phoneNumber).length > 20) {
          errors.buttons = `Call Button #${idx}: phone number must be 20 characters or less.`;
          break;
        }
        if (!/^[0-9+\-\s()]+$/.test(String(b.phoneNumber))) {
          errors.buttons = `Call Button #${idx}: phone number contains invalid characters.`;
          break;
        }
      }
      if (b.type === 'COPY') {
        const code = b.sample || b.coupon_code || b.example;
        if (!code || String(code).trim() === '') {
          errors.buttons = `Copy Code #${idx}: code is required (max 15 chars).`;
          break;
        }
        if (String(code).length > 15) {
          errors.buttons = `Copy Code #${idx}: code must be 15 characters or less.`;
          break;
        }
      }
    }
  }

  return errors;
};

export const validateVariableSamples = (formData) => {
  const headerVars = extractVars(formData.headerText || '');
  const bodyVars = extractVars(formData.bodyText || '');
  const allVars = Array.from(new Set([...(headerVars || []), ...(bodyVars || [])]));

  const missing = allVars.filter(v => !formData.sampleValues[v] || String(formData.sampleValues[v]).trim() === '');

  if (missing.length) {
    return {
      valid: false,
      missing,
      message: `Please provide sample values for: ${missing.join(', ')}`
    };
  }

  const sampleErrors = Object.values(formData.sampleErrors || {}).filter(Boolean);
  if (sampleErrors.length) {
    return {
      valid: false,
      message: 'Please fix the sample input errors'
    };
  }

  return { valid: true };
};

export const getVarValidationErrors = (headerVars = [], bodyVars = []) => {
  const errors = {};

  const headerError = validateVarsByType(headerVars);
  if (headerError) errors.header = `Header: ${headerError}`;

  const bodyError = validateVarsByType(bodyVars);
  if (bodyError) errors.body = `Body: ${bodyError}`;

  return errors;
};
